export type LevelAxis = 'roll' | 'pitch';

export interface OrientationReading {
  alpha?: number | null;
  beta?: number | null;
  gamma?: number | null;
  webkitCompassHeading?: number | null;
}

export type LevelQuality = 'level' | 'near' | 'out';
export type CompassStability = 'unavailable' | 'calibrating' | 'steady' | 'unstable';
export type SquareAlignGuidanceStatus =
  | 'unavailable'
  | 'needs-reference'
  | 'calibrating'
  | 'unstable'
  | 'aligned'
  | 'turn-clockwise'
  | 'turn-counterclockwise';

export interface SquareAlignGuidance {
  status: SquareAlignGuidanceStatus;
  label: string;
  detail: string;
  delta?: number;
}

export function rawLevelAngle(reading: OrientationReading, axis: LevelAxis): number | null {
  const value = axis === 'roll' ? reading.gamma : reading.beta;
  return typeof value === 'number' && Number.isFinite(value) ? normalizeLevelAngle(value) : null;
}

/**
 * Left-right tilt (roll), in degrees, from the raw gravity vector — the
 * inclination of the device's left-right (x) axis above/below horizontal.
 *
 * roll = atan2(x, hypot(y, z)). Front-back tilt only moves gravity between the
 * y and z axes, never onto x, so it is ignored — and this holds for ANY way the
 * phone is held (flat or upright against conduit), unlike DeviceOrientation
 * `gamma`, which gimbal-locks near vertical. x/y/z are the components of
 * DeviceMotion.accelerationIncludingGravity (x = right, y = top, z = out of screen).
 */
export function rollFromGravity(
  x: number | null | undefined,
  y: number | null | undefined,
  z: number | null | undefined,
): number | null {
  if (
    typeof x !== 'number' ||
    !Number.isFinite(x) ||
    typeof y !== 'number' ||
    !Number.isFinite(y) ||
    typeof z !== 'number' ||
    !Number.isFinite(z) ||
    (x === 0 && y === 0 && z === 0)
  ) {
    return null;
  }
  return normalizeLevelAngle((Math.atan2(x, Math.hypot(y, z)) * 180) / Math.PI);
}

export function zeroLevelAngle(rawAngle: number, zeroOffset: number): number {
  return normalizeLevelAngle(rawAngle - zeroOffset);
}

export function rawHeadingAngle(reading: OrientationReading): number | null {
  const heading =
    typeof reading.webkitCompassHeading === 'number' &&
    Number.isFinite(reading.webkitCompassHeading)
      ? reading.webkitCompassHeading
      : reading.alpha;
  return typeof heading === 'number' && Number.isFinite(heading)
    ? normalizeCompassHeading(heading)
    : null;
}

export function compassTargetHeading(referenceHeading: number, targetOffset: number): number {
  return normalizeCompassHeading(referenceHeading + targetOffset);
}

export function compassDeltaAngle(
  currentHeading: number,
  referenceHeading: number,
  targetOffset: number,
): number {
  return normalizeLevelAngle(compassTargetHeading(referenceHeading, targetOffset) - currentHeading);
}

export function formatCompassHeading(heading: number): string {
  return `${Math.round(normalizeCompassHeading(heading)) % 360}\u00b0`;
}

export function compassHeadingSpread(headings: readonly number[]): number {
  const normalized = headings
    .filter((heading) => Number.isFinite(heading))
    .map(normalizeCompassHeading)
    .sort((a, b) => a - b);
  if (normalized.length <= 1) return 0;

  let largestGap = 0;
  for (let index = 0; index < normalized.length; index += 1) {
    const current = normalized[index] ?? 0;
    const next =
      (normalized[(index + 1) % normalized.length] ?? 0) +
      (index === normalized.length - 1 ? 360 : 0);
    largestGap = Math.max(largestGap, next - current);
  }

  return Math.round((360 - largestGap) * 10) / 10;
}

export function compassStability(headings: readonly number[], stableSpread = 8): CompassStability {
  const validHeadings = headings.filter((heading) => Number.isFinite(heading));
  if (validHeadings.length === 0) return 'unavailable';
  if (validHeadings.length < 3) return 'calibrating';
  return compassHeadingSpread(validHeadings) <= stableSpread ? 'steady' : 'unstable';
}

export function squareAlignGuidance(
  currentHeading: number | null,
  referenceHeading: number | null,
  targetOffset: number,
  stability: CompassStability,
  tolerance = 1,
): SquareAlignGuidance {
  if (currentHeading === null) {
    return {
      status: 'unavailable',
      label: 'Compass unavailable',
      detail: 'Start the compass and keep the phone away from metal.',
    };
  }
  if (referenceHeading === null) {
    return {
      status: 'needs-reference',
      label: 'Set wall reference',
      detail: 'Zero to a wall, beam, or control line first.',
    };
  }
  if (stability === 'calibrating') {
    return {
      status: 'calibrating',
      label: 'Compass calibrating',
      detail: 'Hold steady for a couple readings before trusting the reference.',
    };
  }
  if (stability === 'unstable') {
    return {
      status: 'unstable',
      label: 'Compass unstable',
      detail: 'Move away from steel or recalibrate before trusting square layout.',
    };
  }
  if (stability === 'unavailable') {
    return {
      status: 'unavailable',
      label: 'Compass unavailable',
      detail: 'Start the compass and keep the phone away from metal.',
    };
  }

  const delta = roundAngle(compassDeltaAngle(currentHeading, referenceHeading, targetOffset), 1);
  const runLabel = isSquareTarget(targetOffset) ? 'square' : 'parallel';
  if (Math.abs(delta) <= tolerance) {
    return {
      status: 'aligned',
      label: isSquareTarget(targetOffset) ? 'On square' : 'On parallel',
      detail: `Within ${formatUnsignedAngle(tolerance)} of the building reference. Verify near steel.`,
    };
  }

  return {
    status: delta > 0 ? 'turn-clockwise' : 'turn-counterclockwise',
    label: delta > 0 ? 'Rotate clockwise' : 'Rotate counterclockwise',
    detail: `Turn ${formatUnsignedAngle(Math.abs(delta))} to run ${runLabel}. Verify near steel.`,
    delta,
  };
}

export function normalizeLevelAngle(angle: number): number {
  if (!Number.isFinite(angle)) return 0;
  const normalized = ((((angle + 180) % 360) + 360) % 360) - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export function normalizeCompassHeading(heading: number): number {
  if (!Number.isFinite(heading)) return 0;
  const normalized = ((heading % 360) + 360) % 360;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export function bubbleOffset(angle: number, maxAngle = 45): number {
  if (!Number.isFinite(angle) || maxAngle <= 0) return 0;
  return clamp(angle / maxAngle, -1, 1);
}

export function levelQuality(angle: number, tolerance = 0.3): LevelQuality {
  const abs = Math.abs(angle);
  if (abs <= tolerance) return 'level';
  if (abs <= 2) return 'near';
  return 'out';
}

export function levelDirection(angle: number, axis: LevelAxis, tolerance = 0.3): string {
  if (levelQuality(angle, tolerance) === 'level') return 'Level';
  if (axis === 'roll') return angle > 0 ? 'Right high' : 'Left high';
  return angle > 0 ? 'Top high' : 'Bottom high';
}

export function targetDeltaAngle(currentAngle: number, targetAngle: number): number {
  return normalizeLevelAngle(targetAngle - currentAngle);
}

export function levelTargetHint(
  currentAngle: number,
  targetAngle: number,
  tolerance = 0.3,
): string {
  const delta = targetDeltaAngle(currentAngle, targetAngle);
  if (Math.abs(delta) <= tolerance) return 'On target';
  return `Need ${formatSignedLevelAngle(delta)}`;
}

export function shouldNotifyTargetHit(
  currentAngle: number | null,
  targetAngle: number,
  previousAngle?: number | null,
  tolerance = 0.3,
): boolean {
  if (currentAngle === null || !Number.isFinite(currentAngle)) return false;
  const currentOnTarget = Math.abs(targetDeltaAngle(currentAngle, targetAngle)) <= tolerance;
  if (!currentOnTarget) return false;
  if (previousAngle === null || previousAngle === undefined || !Number.isFinite(previousAngle)) {
    return true;
  }
  return Math.abs(targetDeltaAngle(previousAngle, targetAngle)) > tolerance;
}

export function formatSignedLevelAngle(angle: number, precision = 1): string {
  const rounded = roundAngle(angle, precision);
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(precision)}\u00b0`;
}

function roundAngle(angle: number, precision: number): number {
  if (!Number.isFinite(angle)) return 0;
  const factor = 10 ** precision;
  const rounded = Math.round(angle * factor) / factor;
  return Math.abs(rounded) < 1 / factor ? 0 : rounded;
}

function formatUnsignedAngle(angle: number): string {
  return `${Math.abs(roundAngle(angle, 1)).toFixed(1)}\u00b0`;
}

function isSquareTarget(targetOffset: number): boolean {
  const normalized = normalizeCompassHeading(targetOffset);
  return Math.abs(normalized - 90) < 0.01 || Math.abs(normalized - 270) < 0.01;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
